/** The simulator's clock — stepping a minute around a trading session.
 *
 * Pure arithmetic on "HH:MM" strings, kept out of the page so the awkward
 * parts are testable: clamping at both ends, and the fact that a step lands
 * on a minute the archive may or may not hold.
 *
 * The bounds are the SESSION's, not the archive's. Bars run 09:15 to 15:29
 * (the last minute Kite reports), and a step that would leave that range
 * clamps rather than refusing — a reader dragging the clock to the end of
 * the day means "take me to the close", not "do nothing".
 */

/** First SELECTABLE minute of the session.
 *
 * ⚠️ NOT the first bar. The archive genuinely holds a 09:15 bar — every
 * session is 376 bars from 09:15 to 15:30 — but the operator ruled on
 * 2026-09-04 that the simulator starts at 09:16, so the opening minute is
 * out of reach on purpose. Widen this and the picker widens with it.
 */
export const SESSION_OPEN = "09:16";
/** Last bar. 15:29, not 15:30: the 15:30 minute never prints. */
export const SESSION_CLOSE = "15:29";

const MINUTES_IN_HOUR = 60;

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * MINUTES_IN_HOUR + (m ?? 0);
}

export function fromMinutes(total: number): string {
  const h = Math.floor(total / MINUTES_IN_HOUR);
  const m = total % MINUTES_IN_HOUR;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Move `current` by `delta` minutes, clamped to the session.
 *
 * Clamping rather than refusing is deliberate: the step buttons are how a
 * reader scrubs, and a -2h click at 09:30 should land on the open rather
 * than do nothing and look broken.
 *
 * ⭐ `floor` RAISES THE LOWER BOUND ABOVE THE OPEN once a position exists.
 * You cannot un-take a trade: stepping back past its entry would put the
 * clock at a minute where the basket on screen did not yet exist, and the
 * MTM curve already refuses to credit it there. The floor is the EARLIEST
 * entry in the basket, so later legs still appear and disappear as the
 * clock crosses them — it is only the first one that pins the wall.
 */
export function step(current: string, delta: number, floor: string = SESSION_OPEN): string {
  const low = Math.max(toMinutes(SESSION_OPEN), toMinutes(floor));
  const close = toMinutes(SESSION_CLOSE);
  return fromMinutes(Math.min(close, Math.max(low, toMinutes(current) + delta)));
}

/** True when the clock cannot move further that way — the button is dead and
 * should look it. */
export function atLimit(current: string, delta: number, floor: string = SESSION_OPEN): boolean {
  return step(current, delta, floor) === current;
}

/** Pull a typed minute back inside the session, and inside the floor.
 *
 * The time field can be typed into directly, so it needs the same wall the
 * step buttons have — otherwise the one control that bypasses them becomes
 * the way to reach a minute before the position existed.
 */
export function clampTo(at: string, floor: string = SESSION_OPEN): string {
  return step(at, 0, floor);
}

/** The earliest minute any leg was entered, or null for an empty basket.
 *
 * ⭐ THIS IS WHAT STOPS THE CLOCK GOING BEHIND A POSITION. Taking a trade
 * at 11:01 makes 11:00 a minute where the basket on screen did not exist;
 * the MTM curve already refuses to credit it there, so the controls must
 * not offer it either. Only the FIRST entry pins the wall — later legs
 * still appear and disappear as the clock crosses them.
 *
 * Legs with no `entry_at` mean "held all session" and so pin nothing —
 * the same reading the MTM walk gives them.
 */
export function earliestEntry(
  legs: readonly { entry_at?: string | null | undefined }[],
): string | null {
  const stamped = legs.map((l) => l.entry_at).filter((at): at is string => Boolean(at));
  return stamped.reduce<string | null>(
    (lowest, at) => (lowest === null || toMinutes(at) < toMinutes(lowest) ? at : lowest),
    null,
  );
}

/** The step buttons, in the order they are shown. Mirrors the layout of the
 * simulator this replaces: coarse jumps outward, fine steps by the clock. */
export interface ClockStep {
  label: string;
  minutes: number;
}

export const BACK_STEPS: readonly ClockStep[] = [
  { label: "-2h", minutes: -120 },
  { label: "-1h", minutes: -60 },
  { label: "-30m", minutes: -30 },
  { label: "-15m", minutes: -15 },
  { label: "-5m", minutes: -5 },
  { label: "-2m", minutes: -2 },
  { label: "-1m", minutes: -1 },
];

export const FORWARD_STEPS: readonly ClockStep[] = [
  { label: "+1m", minutes: 1 },
  { label: "+2m", minutes: 2 },
  { label: "+5m", minutes: 5 },
  { label: "+15m", minutes: 15 },
  { label: "+30m", minutes: 30 },
  { label: "+1h", minutes: 60 },
  { label: "+2h", minutes: 120 },
];

/** Session minutes as "HH:MM", for a time picker that cannot offer a minute
 * outside the session. */
export function sessionMinutes(): string[] {
  const out: string[] = [];
  for (let m = toMinutes(SESSION_OPEN); m <= toMinutes(SESSION_CLOSE); m += 1) {
    out.push(fromMinutes(m));
  }
  return out;
}

/** Move one SESSION at a time, along the archive's own list.
 *
 * A day step is not minute arithmetic: the archive skips weekends and
 * holidays, and each index joined the capture on a different date, so
 * "yesterday" is whatever the previous captured session happens to be
 * rather than the calendar's answer. Stepping the list rather than the
 * date is what keeps a day button from landing on a session that was
 * never captured.
 *
 * `sessions` is expected ascending, as the API returns it. Clamps at both
 * ends, and returns `current` unchanged if it is not in the list at all.
 */
export function stepSession(sessions: readonly string[], current: string, delta: number): string {
  const i = sessions.indexOf(current);
  if (i === -1) return current;
  const next = Math.min(sessions.length - 1, Math.max(0, i + delta));
  return sessions[next] ?? current;
}

/** True when the day step cannot move — first or last captured session. */
export function atSessionLimit(
  sessions: readonly string[],
  current: string,
  delta: number,
): boolean {
  return stepSession(sessions, current, delta) === current;
}

/** What a step should DO, once the session's edges roll into the next day.
 *
 * Operator, 2026-09-04: *"when user click next from 15:29 it go next day
 * 9:16 … same from 9:16 user click back it move to 15:29 previous market
 * open day"*. Scrubbing a multi-month archive by day-then-time was two
 * controls for one intent; the clock now runs continuously across sessions.
 *
 * Returned rather than performed, because the day change and the clock
 * change live in different hooks — the caller applies both.
 *
 * ⚠️ ROLLING BACK IS REFUSED WHILE A BASKET PINS THE FLOOR. `floor` is the
 * earliest entry in the basket and exists so the clock cannot sit at a
 * minute where the trade on screen had not been taken. Rolling to the
 * PREVIOUS session would land far behind it, so at a raised floor the step
 * clamps exactly as before. Forward is unaffected: there is no upper
 * equivalent of that wall.
 */
export interface StepPlan {
  /** -1, 0 or +1 sessions. */
  dayDelta: number;
  /** The clock's new value once `dayDelta` has been applied. */
  at: string;
}

/** Forward off the close, with a later session to land on. */
function rollsForward(current: string, delta: number, hasNext: boolean): boolean {
  return delta > 0 && hasNext && current === SESSION_CLOSE;
}

/** Back off the open, with an earlier session to land on — and no basket
 * pinning the floor above the open. */
function rollsBack(current: string, delta: number, floor: string, hasPrev: boolean): boolean {
  if (delta >= 0 || !hasPrev) return false;
  if (toMinutes(floor) > toMinutes(SESSION_OPEN)) return false;
  return toMinutes(current) <= toMinutes(SESSION_OPEN);
}

export function planStep(
  current: string,
  delta: number,
  floor: string = SESSION_OPEN,
  { hasPrev, hasNext }: { hasPrev: boolean; hasNext: boolean } = {
    hasPrev: false,
    hasNext: false,
  },
): StepPlan {
  const next = step(current, delta, floor);
  if (next !== current) return { dayDelta: 0, at: next };
  // The step did nothing, so we are against a wall. Which one?
  if (rollsForward(current, delta, hasNext)) return { dayDelta: 1, at: SESSION_OPEN };
  if (rollsBack(current, delta, floor, hasPrev)) return { dayDelta: -1, at: SESSION_CLOSE };
  return { dayDelta: 0, at: current };
}
